import {
    Box,
    ClickAwayListener,
    Drawer,
    IconButton,
    List,
    styled,
    Tooltip,
    tooltipClasses,
    TooltipProps,
} from "@mui/material";
import LaluneSidebarLogo from "@/assets/images/lalun-logo-sidebar.svg";
import EnUsLogo from "@/assets/images/en-US.png";
import HeILLogo from "@/assets/images/he-IL.png";
import { useLocation, useNavigate } from "react-router-dom";
import "@/assets/base.css";
import "./styles.css";
import SidebarRootBtn from "../sidebarRootBtn/sidebarRootBtn";
import authStore from "@/stores/authStore";
import api from "@/services/api";
import { useTranslation } from "react-i18next";
import React from "react";
import { checkPermissionsWithoutAlert } from "@/util";
import { PermissionName } from "@/models/apiModels";

const LangTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
))(() => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: "#FFFFFF",
        fontSize: 12,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: "#FFFFFF",
        fontSize: 14,
        fontStyle: "normal",
        fontWeight: 700,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "15px",
        padding: "7px 12px",
        left: authStore.isRtl ? "7px" : "-7px",
        boxShadow: "2px 2px 6px rgba(0, 0, 0, 0.25)",
    },
}));

const SidebarTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
))(() => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: "#7E8EC4",
        fontSize: 12,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: "#7E8EC4",
        fontSize: 14,
        fontStyle: "normal",
        fontWeight: 700,
        display: "flex",
        justifyContent: "center",
        padding: "7px 10px",
        left: authStore.isRtl ? "7px" : "-7px",
    },
}));

const StyledSidebarButton = styled(IconButton)(
    () => `
          &:hover {
            background-color: #405289;
          }
        `
);

interface SideBarProps {
    toggleSideBar?: void | null | Function;
}
const Sidebar = (props: SideBarProps) => {
    const { pathname } = useLocation();
    const drawerWidth = "60px";
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [open, setOpen] = React.useState(false);

    async function changeLang(lang: string) {
        await api.changeLanguage(lang);
        window.location.reload();
    }

    async function logout() {
        await api.logout();
        const res = await api.authInfo();
        authStore.update(res.data);
        navigate("/login");
    }

    function toggleMenu(bool: boolean) {
        if (props.toggleSideBar) {
            props.toggleSideBar(bool);
        }
    }

    const handleTooltipClose = () => {
        setOpen(false);
    };

    const handleTooltipOpen = () => {
        setOpen(true);
    };

    return (
        <Box component="nav" className="sidebar-block">
            <Drawer
                open={true}
                variant="persistent"
                anchor={authStore.isRtl ? "right" : "left"}
                sx={{
                    width: drawerWidth,
                    gap: "10px",
                    "& .MuiDrawer-paper": {
                        backgroundColor: "#405289",
                        boxSizing: "border-box",
                        width: drawerWidth,
                        border: "none",
                        paddingInlineStart: "5px",
                    },
                }}
            >
                <Box width="100%" onClick={() => toggleMenu(false)}>
                    <Box>
                        <div onClick={() => navigate("/")} className="lalune-logo">
                            <img
                                src={LaluneSidebarLogo}
                                alt="LaluneSidebarLogo"
                                style={{ maxWidth: 46, width: "100%" }}
                            />
                        </div>
                    </Box>
                    <List
                        className="mobile-margin"
                        sx={{
                            boxSizing: "border-box",
                            width: "100%",
                            justifyContent: "flex",
                        }}
                    >
                        <SidebarRootBtn
                            isActive={pathname === "/"}
                            buttonClass="icon-HomePageIcon"
                            navigatePath="/"
                            tooltipName={t("breadcrumbs.HomePage")}
                        />
                        {checkPermissionsWithoutAlert(PermissionName.ListOfEmployees) && (
                            <SidebarRootBtn
                                isActive={pathname.includes("/employees")}
                                buttonClass="icon-EmployeesTabLogo"
                                navigatePath="/employees"
                                tooltipName={t("sidebar.Employees")}
                            />
                        )}
                        {checkPermissionsWithoutAlert(PermissionName.ListOfWallets) && (
                            <SidebarRootBtn
                                isActive={pathname.includes("/digital-wallet")}
                                buttonClass="icon-DigitalWalletsTabLogo"
                                navigatePath="/digital-wallet"
                                tooltipName={t("sidebar.DigitalWallet")}
                            />
                        )}
                        {checkPermissionsWithoutAlert(PermissionName.ListOfDocuments) && (
                            <SidebarRootBtn
                                isActive={pathname.includes("/accounting")}
                                buttonClass="icon-AccountDocIconV2"
                                navigatePath="/accounting"
                                tooltipName={t("sidebar.AccDocs")}
                            />
                        )}
                        {checkPermissionsWithoutAlert(PermissionName.ListOfOrders) && (
                            <SidebarRootBtn
                                isActive={pathname.includes("/orders")}
                                buttonClass="icon-OrdersTabLogo"
                                navigatePath="/orders"
                                tooltipName={t("sidebar.Orders")}
                            />
                        )}
                        {checkPermissionsWithoutAlert(PermissionName.ListOfForms) && (
                            <SidebarRootBtn
                                isActive={pathname.includes("/forms")}
                                buttonClass="icon-FormChatIcon"
                                navigatePath="/forms"
                                tooltipName={t("breadcrumbs.FormsChat")}
                            />
                        )}
                        {checkPermissionsWithoutAlert(PermissionName.ListOfMessages) && (
                            <SidebarRootBtn
                                isActive={pathname.includes("/messages")}
                                buttonClass="icon-MessagesIcon"
                                navigatePath="/messages"
                                tooltipName={t("sidebar.Messages")}
                            />
                        )}
                    </List>
                    <Box width="100%">
                        <div className="w-100">
                            <div className="sidebar-divider-block">
                                <div className="sidebar-divider"></div>
                            </div>
                        </div>
                        <List
                            sx={{
                                boxSizing: "border-box",
                                width: "100%",
                                justifyContent: "flex",
                            }}
                        >
                            <ClickAwayListener onClickAway={handleTooltipClose}>
                                <div>
                                    <LangTooltip
                                        title={
                                            <React.Fragment>
                                                <div
                                                    style={{ height: 34 }}
                                                    className={authStore.cultureName === "he-IL" ? "active-lang" : ""}
                                                >
                                                    <img
                                                        className="language-image"
                                                        src={HeILLogo}
                                                        alt="he-IL-lang"
                                                        onClick={() => changeLang("he-IL")}
                                                    />
                                                </div>
                                                <div
                                                    style={{ height: 34 }}
                                                    className={authStore.cultureName === "en-US" ? "active-lang" : ""}
                                                >
                                                    <img
                                                        className="language-image"
                                                        src={EnUsLogo}
                                                        alt="en-US-lang"
                                                        onClick={() => changeLang("en-US")}
                                                    />
                                                </div>
                                            </React.Fragment>
                                        }
                                        placement="right"
                                        disableFocusListener
                                        disableHoverListener
                                        disableTouchListener
                                        onClose={handleTooltipClose}
                                        open={open}
                                    >
                                        <StyledSidebarButton
                                            onClick={handleTooltipOpen}
                                            className={"customSideBtn"}
                                            sx={{ marginBottom: "10px" }}
                                        >
                                            <i className={"icon-f icon-LanguageTabLogo fs-26 customSideBtnIcon"} />
                                        </StyledSidebarButton>
                                    </LangTooltip>
                                </div>
                            </ClickAwayListener>

                            <SidebarRootBtn
                                isActive={pathname.includes("/settings")}
                                buttonClass="icon-SettingTabLogo"
                                navigatePath="/settings"
                                tooltipName={t("sidebar.Settings")}
                                showMenu={toggleMenu}
                            />
                            <SidebarTooltip title={t("sidebar.Exit")} placement="right">
                                <StyledSidebarButton className={"customSideBtn"} onClick={() => logout()}>
                                    <i className={"icon-f icon-LogoutTabLogo fs-26 customSideBtnIcon"} />
                                </StyledSidebarButton>
                            </SidebarTooltip>
                        </List>
                    </Box>
                </Box>
            </Drawer>
        </Box>
    );
};

export default Sidebar;
